/**
 * Session-scoped one-shot guard key. Set the first time we auto-reload for a failed lazy-chunk
 * load, checked before reloading again — so a chunk that is genuinely gone (or an origin that is
 * offline) can never trap the tab in a reload loop. Session-scoped on purpose: it must survive the
 * reload it triggers (same tab) yet reset for a fresh session.
 */
export const RELOAD_GUARD_KEY = 'vn-pwa:chunk-reload'

// A failed `import()` of a lazy chunk surfaces as one of these messages — almost always after a
// redeploy changed the content-hashed filenames while an old shell was still open. Mirrors the
// ui-kit `isDynamicImportError` detection, kept self-contained so the install-critical path pulls
// in no heavy shared dependency.
const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome / Chromium (incl. Chrome iOS)
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari / WebKit
  /Unable to preload CSS/i, // Vite: a hashed CSS asset for the chunk 404s post-deploy
  /Loading chunk \d+ failed/i, // webpack-style chunk failure (future-proofing)
]

/** Whether a thrown value is a lazy-chunk load failure (as opposed to an ordinary render bug). */
export const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message))
}

/** What the boundary should do with a caught error. */
export type RecoveryAction = 'fallback' | 'reload'

/** The minimal Storage surface the guard touches — lets tests inject an in-memory fake. */
type GuardStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>

/**
 * Stand-in used when session storage is unreachable (a sandboxed in-app / WebView browser, private
 * mode). It reads as PERMANENTLY armed so {@link decideRecovery} degrades to `fallback` and never
 * auto-reloads — a reload we cannot remember having done would loop the tab forever. Its writes are
 * no-ops. This is the whole app's shield against `window.sessionStorage` access throwing.
 */
const UNAVAILABLE_STORAGE: GuardStorage = {
  getItem: () => '1',
  removeItem: () => undefined,
  setItem: () => undefined,
}

/**
 * Resolve the guard store, never throwing. In the target in-app/WebView browsers even *reading*
 * `window.sessionStorage` can raise a SecurityError, so both the injected value and the property
 * access are handled: tests inject a fake; production reads session storage, falling back to the
 * never-reload {@link UNAVAILABLE_STORAGE} if it is missing or its getter throws.
 */
export const resolveGuardStorage = (injected?: GuardStorage): GuardStorage => {
  if (injected) return injected
  try {
    return globalThis.sessionStorage ?? UNAVAILABLE_STORAGE
  } catch {
    return UNAVAILABLE_STORAGE
  }
}

/**
 * Decide how to recover from an error caught around the lazy auth screens:
 *  - a chunk-load failure not yet retried this session → arm the guard and `reload` (the fresh
 *    HTML, now served network-first, carries the current asset manifest so the retry resolves);
 *  - the same failure after a reload already happened, or ANY non-chunk render error → `fallback`,
 *    so the user gets a real error surface instead of a white screen or a reload loop.
 *
 * Every storage touch is guarded: if reading OR writing throws (blocked storage), we return
 * `fallback` — never an auto-reload we can't record, which would loop.
 */
export const decideRecovery = (storage: GuardStorage, error: unknown): RecoveryAction => {
  if (!isChunkLoadError(error)) return 'fallback'
  try {
    if (storage.getItem(RELOAD_GUARD_KEY) !== null) return 'fallback'
    storage.setItem(RELOAD_GUARD_KEY, '1')
  } catch {
    return 'fallback'
  }
  return 'reload'
}

/**
 * Disarm the guard once the chunk has loaded successfully, re-enabling one auto-reload later. Runs
 * on every successful login mount, so it swallows a throwing store: an error escaping here would
 * bubble out of the effect and tear down the tree — the very white screen this module prevents.
 */
export const clearReloadGuard = (storage: GuardStorage): void => {
  try {
    storage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // Storage is blocked; there is nothing to disarm and nothing safe to do.
  }
}
